import { State, StateAction } from '@people-science/state-machine';
import { DynamicFormContextState } from '../../DynamicFormContext';
import { TransitionConditionPlugin } from '../TransitionConditionPlugin';
import { User } from '../../../models/User';
import {
  differenceInYears,
  isAfter,
  isBefore,
  isSameDay,
  isValid
} from 'date-fns';

export enum UserDataFieldConditionOperator {
  Equal = 'equal',
  NotEqual = 'not-equal',
  HasValue = 'has-value',
  ContainsValue = 'contains-value',
  GreaterThan = 'greater-than',
  GreaterThanOrEqual = 'greater-than-or-equal',
  LessThan = 'less-than',
  LessThanOrEqual = 'less-than-or-equal',
  After = 'after',
  Before = 'before'
}

export interface UserDataFieldCondition {
  fieldKey: string;
  operator: UserDataFieldConditionOperator;
  values?: (string | number | boolean | Date)[];
}

export function firstValidBirthDate(
  ...candidates: unknown[]
): Date | undefined {
  for (const candidate of candidates) {
    if (candidate == null || candidate === '') continue;
    const date =
      candidate instanceof Date ? candidate : new Date(`${candidate}`);
    if (isValid(date)) return date;
  }
  return undefined;
}

type TransitionEvent = {
  name?: string;
  isTransient?: boolean;
  transitions?: Array<{
    conditions?: Array<{
      type?: string;
      params?: { fieldKey?: string };
    } | null> | null;
  } | null> | null;
};

const eventUsesAge = (event: TransitionEvent) =>
  Boolean(
    event.transitions?.some(transition =>
      transition?.conditions?.some(
        condition =>
          condition?.type === 'User Data Field Condition' &&
          condition.params?.fieldKey === 'age'
      )
    )
  );

export function missingBirthDateBlocksTransition(options: {
  events?: TransitionEvent[] | null;
  nextEvents?: Array<{ name?: string; canTransition?: boolean }> | null;
  birthDateCandidates: unknown[];
}): boolean {
  if (firstValidBirthDate(...options.birthDateCandidates)) return false;
  const visible = (options.events ?? []).filter(event => !event.isTransient);
  const blockedByBirthDate = visible.filter(eventUsesAge);
  if (!blockedByBirthDate.length) return false;
  const canTransition = (event: TransitionEvent) =>
    options.nextEvents?.find(next => next.name === event.name)?.canTransition;
  if (visible.some(event => canTransition(event))) return false;
  return blockedByBirthDate.every(event => canTransition(event) === false);
}

export const UserDataFieldConditionPlugin: TransitionConditionPlugin<UserDataFieldCondition> =
  {
    type: 'User Data Field Condition',
    validate: (condition: Partial<StateAction<UserDataFieldCondition>>) => {
      return true;
    },
    guard: (
      state: State<DynamicFormContextState<any, any>>,
      params: UserDataFieldCondition
    ) => {
      const user = state.context.participantUser ?? state.context.user;
      const birthDate = firstValidBirthDate(
        (state.context.formValues as { birthDate?: unknown } | undefined)
          ?.birthDate,
        user?.birthDate
      );
      const value = {
        ...user,
        birthDate,
        age: birthDate ? differenceInYears(new Date(), birthDate) : undefined
      }[params.fieldKey as keyof User] as any;
      switch (params?.operator) {
        case UserDataFieldConditionOperator.HasValue:
          if (params.values && params.values.length > 0) {
            // check if at lease X number of items selected
            return Array.isArray(value) && value.length >= +params.values;
          } else if (Array.isArray(value)) {
            return value.length > 0;
          } else {
            return typeof value !== 'undefined';
          }
        case UserDataFieldConditionOperator.ContainsValue: {
          const values = Array.isArray(value) ? value : [value];
          const paramValues = params.values || [];
          return paramValues.every(pv => values.some(value => value === pv));
        }
        case UserDataFieldConditionOperator.NotEqual: {
          const values = Array.isArray(value) ? value : [value];
          if (values.length === 0) return false;
          for (const value of values) {
            for (const paramValue of params.values || []) {
              if (params.fieldKey === 'birthDate') {
                if (isSameDay(new Date(paramValue as any), value)) {
                  return false;
                }
              } else {
                // using non-strict equality
                if (paramValue == value) {
                  return false;
                }
              }
            }
          }
          return true;
        }
        case UserDataFieldConditionOperator.GreaterThan:
          return value > +(params.values?.[0] || 0);
        case UserDataFieldConditionOperator.GreaterThanOrEqual:
          return value >= +(params.values?.[0] || 0);
        case UserDataFieldConditionOperator.LessThan:
          return value < +(params.values?.[0] || 0);
        case UserDataFieldConditionOperator.LessThanOrEqual:
          return value <= +(params.values?.[0] || 0);
        case UserDataFieldConditionOperator.After:
          return isAfter(value, new Date(params.values?.[0] as any));
        case UserDataFieldConditionOperator.Before:
          return isBefore(value, new Date(params.values?.[0] as any));
        default: {
          const values = Array.isArray(value) ? value : [value];
          if (values.length === 0) return false;
          for (const value of values) {
            for (const paramValue of params.values || []) {
              if (params.fieldKey === 'birthDate') {
                if (!isSameDay(new Date(paramValue as any), value)) {
                  return false;
                }
              } else {
                // using non-strict equality
                if (paramValue != value) {
                  return false;
                }
              }
            }
          }
          return true;
        }
      }
    }
  };
